import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { STAUTES } from "../../../../utils/constant";
import { successToast } from "../../../../utils/toastHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import useColumn from "../../../../utils/customHooks/useColumns";
import { DELETE_ADMIN_USER, GET_ADMIN_USERS } from "../../../../utils/services/apiPath";
import { deleteApi, postApi } from "../../../../utils/services";
import { useTheme } from "../../../ContextStore/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout, userSelector } from "../../../store/features/auth/authSlice";
import logo from "../../../Assets/Icons/logo.png";
import logoWhite from "../../../Assets/Icons/logo-white.png";
import menuopen from "../../../Assets/Icons/menu-open.png";
import menuclose from "../../../Assets/Icons/menu-close.png";
import NotificationIcon from "../../../Assets/IconComponents/Notification";
import { headerMenus } from "../../../../utils/MenusConfig";
import HelpMenu from "../../../Components/Header/HelpMenu";
import {ProGrid, NameCell, DeleteCell, Layout} from "@proscore-ai/proscore-ui-library";
import useCsvExport from "../../../../utils/useCsvExporter";

const getRedirectUrl = params => {
  return `/users/${params.id}`;
};

export default function AdminUserList() {
  const navigation = useNavigate();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [popOverOpen, setPopOverOpen] = useState(false);
  const ref = useRef(null);
  const userData = useSelector(userSelector);
  const dispatch = useDispatch();

  const handleTabClick = tab => {
    navigate(`${tab.to}`);
  };
  const handleMenuOpen = () => setMenuOpen(!menuOpen);

  const onLogout = () => {
    dispatch(handleLogout());
  };

  const manageAccount = () => {
    setPopOverOpen(false);
  };

  const handleHome = () => {
    navigate("/clients");
  };
  const {
    currentPage,
    pageSize,
    data: adminUsersData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const columns = useColumn(
    [
      {
        field: "firstName",
        headerName: "First Name",
        renderCell: params => <NameCell params={params} getRedirectUrl={getRedirectUrl} />
      },
      {
        field: "lastName",
        headerName: "Last Name"
      },
      {
        field: "email",
        headerName: "Email"
      },
      {
        field: "userName",
        headerName: "User Name"
      },
      {
        field: "userGuid",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} />
      }
    ],
    true
  );

  const onDelete = async id => {
    const { error } = await deleteApi(DELETE_ADMIN_USER, { params: { adminUserId: id } });
    if (error) return errorToast(error);
    getAdminUsers();
    successToast("Admin User deleted successfully.");
  };

  const addButtonAction = () => {
    navigation("/users/add");
  };

  const getAdminUsers = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "firstName",
      ...sortModel
    };
    const { data, error, totalRecords } = await postApi(GET_ADMIN_USERS, payload);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const handleDownloadCSV = useCsvExport({
  api: GET_ADMIN_USERS,

  getPayload: () => {
    const activeSort = sortModel?.[0] || {
      field: "firstName",
      sort: "asc"
    };

    return {
      pageIndex: 1,
      pageSize: rowCount || 10000,
      searchString,
      orderByAscending: activeSort.sort === "asc",
      orderCol: activeSort.field
    };
  },

  mapData: data =>
    data.map(item => ({
      "First Name": item.firstName || "",
      "Last Name": item.lastName || "",
      "Email": item.email || "",
      "User Name": item.userName || ""
    })),

  fileName: "AdminUsersList.csv"
});

  useEffect(() => {
    getAdminUsers();
  }, [getAdminUsers]);

  return (
    <Layout canView={true}
    theme={theme}
    user={{
      userName: userData?.userName,
      email: userData?.email,
      avatar: userData?.avatar
    }}
    onLogout={onLogout}
    onTabClick={handleTabClick}
    onLogoClick={handleHome}
    logo={logo}
    logoWhite={logoWhite}
    menuOpenIcon={menuopen}
    menuCloseIcon={menuclose}
    menuTabs={headerMenus}
    NotificationIcon={NotificationIcon}
    HelpComponent={HelpMenu}
    >
      <ProGrid
        title="Admin User"
        columns={columns}
        loading={status === STAUTES.LOADING}
        rows={adminUsersData}
        searchMode={!!searchString}
        searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        addButtonLabel="+ Add Admin User"
        addButtonAction={addButtonAction}
        exportAction={handleDownloadCSV}
        options={{
          getRowId: row => row.userGuid,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          onSortModelChange: setSortModel
        }}
      />
    </Layout>
  );
}
